import React, { useState, useEffect } from 'react';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';
import CategorySelect from './CategorySelect';

const ServiceFormModal = ({ isOpen, mode, serviceData, onClose, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [workingDays, setWorkingDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    pricing_type: 'fixed',
    category_id: '',
    user_id: '',
  });

  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      if (mode === 'edit' && serviceData) {
        loadServiceData();
      } else {
        resetForm();
      }
    }
  }, [isOpen, mode]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [cats, vens, tgs] = await Promise.all([
        httpClient.get(ENDPOINTS.CATEGORIES, null, { useCache: true }),
        httpClient.get(`${ENDPOINTS.ADMIN_API}/vendors?per_page=500`, null, { useCache: true }),
        httpClient.get(ENDPOINTS.TAGS, null, { useCache: true }),
      ]);

      const categoriesData = Array.isArray(cats) ? cats : (Array.isArray(cats?.data) ? cats.data : []);
      const vendorsData = Array.isArray(vens) ? vens : (Array.isArray(vens?.data) ? vens.data : (Array.isArray(vens?.data?.data) ? vens.data.data : []));
      const tagsData = Array.isArray(tgs) ? tgs : (Array.isArray(tgs?.data) ? tgs.data : []);

      setCategories(categoriesData);
      setVendors(vendorsData);
      setAllTags(tagsData);
    } catch (err) {
      setError('Failed to load form data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadServiceData = async () => {
    try {
      const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/services/${serviceData.id}`);
      const service = response?.data || response;
      
      setFormData({
        name: service.name || '',
        description: service.description || '',
        price: service.price || '',
        pricing_type: service.pricing_type || 'fixed',
        category_id: service.category_id || '',
        user_id: service.user_id || '',
      });

      if (service.tags) {
        setSelectedTags(Array.isArray(service.tags) ? service.tags : []);
      }

      if (service.hours) {
        const hoursMatch = service.hours.match(/(\w+(?:,\s*\w+)*)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
        if (hoursMatch) {
          setWorkingDays(hoursMatch[1].split(', '));
          setTimeRange({ start: hoursMatch[2], end: hoursMatch[3] });
        }
      }
    } catch (err) {
      setError('Failed to load service details');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', pricing_type: 'fixed', category_id: '', user_id: '' });
    setSelectedTags([]);
    setTagSearch('');
    setFilteredTags([]);
    setWorkingDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
    setTimeRange({ start: '09:00', end: '18:00' });
    setError('');
  };

  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag && tag.name && tag.name.toLowerCase().includes(search) &&
        !selectedTags.some(selected => selected && selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 5));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, selectedTags]);

  const toggleDay = (day) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
    setTagSearch('');
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(prev => prev.filter(t => (t.id ? t.id !== tag.id : t.name !== tag.name)));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          handleTagSelect(existingTag);
        } else {
          setSelectedTags(prev => [...prev, { id: null, name: tagName }]);
          setTagSearch('');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id) {
      setError('Please select a vendor');
      return;
    }

    setSubmitting(true);
    const hoursFormatted = workingDays.length > 0
      ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
      : 'Closed / Appointment Only';

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: formData.price,
        pricing_type: formData.pricing_type,
        category_id: parseInt(formData.category_id),
        user_id: parseInt(formData.user_id),
        hours: hoursFormatted,
        tags: selectedTags.map(tag => ({ id: tag.id || null, name: tag.name })),
      };

      if (mode === 'add') {
        await httpClient.post(`${ENDPOINTS.ADMIN_API}/services`, payload);
      } else {
        await httpClient.put(`${ENDPOINTS.ADMIN_API}/services/${serviceData.id}`, payload);
      }

      httpClient.clearCache();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.title}>{mode === 'add' ? '➕ Add Service' : '✏️ Edit Service'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Service Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Price *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Pricing Type *</label>
              <select
                value={formData.pricing_type}
                onChange={(e) => setFormData({ ...formData, pricing_type: e.target.value })}
                required
                style={styles.input}
              >
                <option value="fixed">✅ Fixed Price</option>
                <option value="starting">📈 Starting Price</option>
                <option value="visiting">🚗 Visiting Charges</option>
                <option value="subscription">🔄 Subscription</option>
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Category *</label>
              <CategorySelect
                categories={categories}
                value={formData.category_id}
                onChange={(catId) => setFormData({ ...formData, category_id: catId })}
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Vendor *</label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                required
                style={styles.input}
              >
                <option value="">Select Vendor</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              style={{...styles.input, fontFamily: 'inherit'}}
            />
          </div>

          <div style={{ ...styles.inputGroup, marginTop: '16px' }}>
            <label>Working Days</label>
            <div style={styles.daysContainer}>
              {daysList.map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  style={{
                    ...styles.dayBtn,
                    background: workingDays.includes(day) ? '#4f46e5' : 'white',
                    color: workingDays.includes(day) ? 'white' : '#1e293b',
                    border: `1px solid ${workingDays.includes(day) ? '#4f46e5' : '#e2e8f0'}`,
                  }}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Opens At</label>
              <input
                type="time"
                value={timeRange.start}
                onChange={(e) => setTimeRange({ ...timeRange, start: e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Closes At</label>
              <input
                type="time"
                value={timeRange.end}
                onChange={(e) => setTimeRange({ ...timeRange, end: e.target.value })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Tags</label>
            <div style={styles.tagSearchBox}>
              <input
                type="text"
                placeholder="Search or type tag name..."
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                style={{...styles.input, marginBottom: '8px'}}
              />
              {filteredTags.length > 0 && (
                <div style={styles.suggestionBox}>
                  {filteredTags.map(tag => (
                    <div
                      key={tag.id}
                      onClick={() => handleTagSelect(tag)}
                      style={styles.suggestionItem}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.tagsContainer}>
              {selectedTags.map(tag => (
                <span key={tag.id || tag.name} style={styles.tag}>
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => handleTagRemove(tag)}
                    style={{ marginLeft: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5' }}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
            <button type="submit" disabled={submitting} style={styles.submitBtn}>
              {submitting ? 'Submitting...' : (mode === 'add' ? 'Create Service' : 'Update Service')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #f1f5f9',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#64748b',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    color: '#0f172a',
    backgroundColor: '#fff',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
  },
  daysContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dayBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    transition: '0.2s',
  },
  tagSearchBox: {
    position: 'relative',
  },
  suggestionBox: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 10,
    maxHeight: '200px',
    overflowY: 'auto',
  },
  suggestionItem: {
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1e293b',
    borderBottom: '1px solid #f1f5f9',
    transition: '0.15s',
    backgroundColor: '#fff',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  tag: {
    backgroundColor: '#eef2ff',
    color: '#4f46e5',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    border: '1px solid #fee2e2',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    fontWeight: 600,
    transition: '0.2s',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#4f46e5',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    transition: '0.2s',
  },
};

export default ServiceFormModal;
